/** The weekly status report.
 *
 * Written to be read top to bottom in a leadership meeting: the numbers, then what needs
 * attention, then the work in flight. "Needs attention" comes before the good news
 * deliberately — a report that opens with everything that is fine buries the one project
 * that is not.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Printer } from 'lucide-react'

import {
  Button,
  EmptyState,
  ErrorState,
  Loading,
  Pill,
  SectionHeader,
  StatCard,
} from '@/components/ui'
import { formatDate, ragTone } from '@/lib/domain'
import { useStatusReport } from '@/features/reports/useReports'

export function StatusReportPage() {
  const navigate = useNavigate()
  const [days, setDays] = useState(7)
  const { data, isPending, error } = useStatusReport(days)

  if (isPending) return <Loading label="Building the report" />
  if (error) return <ErrorState error={error} />
  if (!data) return null

  const rag = data.byRag

  return (
    <div className="mx-auto max-w-[1100px] pt-2">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl text-fg">Status report</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Generated {new Date(data.generatedAt).toLocaleString()} · last {data.periodDays} days
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <select
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            aria-label="Reporting period"
          >
            {[7, 14, 30].map((value) => (
              <option key={value} value={value}>
                Last {value} days
              </option>
            ))}
          </select>
          {/* Printing is how this reaches people who do not sign in. */}
          <Button onClick={() => window.print()}>
            <Printer size={14} aria-hidden />
            Print
          </Button>
        </div>
      </header>

      <div className="mb-8 flex flex-wrap gap-3">
        {/* Each number opens the Projects list filtered to exactly the projects behind it.
            The filters live in the URL, so these are ordinary links — and the resulting
            view can be pasted to whoever asked. */}
        <StatCard
          label="Projects"
          value={data.totals.projects}
          tone="info"
          hint="Open the full portfolio"
          onClick={() => navigate('/projects')}
        />
        <StatCard
          label="Active"
          value={data.totals.active}
          tone="green"
          hint="Open the active projects"
          onClick={() => navigate('/projects?status=active')}
        />
        <StatCard
          label="Need attention"
          value={data.totals.atRisk}
          tone="amber"
          hint="Open the amber and red projects"
          onClick={() => navigate('/projects?ragStatus=at_risk')}
        />
        <StatCard
          label="Past target"
          value={data.totals.overdue}
          tone="red"
          hint="Open the projects past their target date"
          onClick={() => navigate('/projects?overdue=true')}
        />
        <StatCard
          label="Complete"
          value={data.totals.complete}
          tone="neutral"
          hint="Open the completed projects"
          onClick={() => navigate('/projects?status=complete')}
        />
      </div>

      {/* Attention first. A report that opens with what is fine buries what is not. */}
      <SectionHeader>Needs attention</SectionHeader>
      {data.attention.length === 0 ? (
        <EmptyState title="Nothing flagged">
          No project is amber, red, or past its target date.
        </EmptyState>
      ) : (
        <ul className="card divide-y divide-border">
          {data.attention.map((project) => (
            <li key={project.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <AlertTriangle
                size={16}
                aria-hidden
                style={{
                  color:
                    project.ragStatus === 'red' ? 'var(--status-red)' : 'var(--status-amber)',
                }}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-fg">{project.name}</div>
                <div className="text-xs text-fg-muted">
                  {project.ownerName || 'No owner'} · {project.percentComplete}% complete
                </div>
              </div>
              {project.ragStatus !== 'none' ? (
                <Pill tone={ragTone(project.ragStatus)}>{project.ragStatus}</Pill>
              ) : null}
              {project.overdue ? <Pill tone="red">Past target</Pill> : null}
              <span className="text-sm text-fg-muted">{formatDate(project.targetDate)}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-8">
        <SectionHeader>Portfolio health</SectionHeader>
      </div>
      <div className="card p-4">
        <div className="flex h-6 w-full overflow-hidden rounded-full">
          {(['green', 'amber', 'red', 'none'] as const).map((key) => {
            const count = rag[key] ?? 0
            if (count === 0) return null
            const share = (count / Math.max(1, data.totals.projects)) * 100
            return (
              <div
                key={key}
                style={{
                  width: `${share}%`,
                  backgroundColor:
                    key === 'none' ? 'var(--status-neutral)' : `var(--status-${key})`,
                }}
                title={`${key}: ${count}`}
              />
            )
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-fg-muted">
          {/* The legend says what a colour MEANS, not what colour it is. */}
          {(
            [
              ['green', 'On track'],
              ['amber', 'At risk'],
              ['red', 'Behind'],
              ['none', 'No health set'],
            ] as const
          ).map(([key, label]) => (
            <span key={key}>
              {label}: {rag[key] ?? 0}
            </span>
          ))}
        </div>
      </div>

      {data.sprint ? (
        <>
          <div className="mt-8">
            <SectionHeader>Current sprint</SectionHeader>
          </div>
          <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <div className="font-medium text-fg">{data.sprint.name}</div>
              <div className="text-xs text-fg-muted">
                Ends {formatDate(data.sprint.endDate)} · {data.sprint.daysRemaining} days left
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-2xl text-fg">
                {data.sprint.donePoints} / {data.sprint.committedPoints}
              </div>
              <div className="micro-label text-fg-muted">
                points · {data.sprint.percentComplete}%
              </div>
            </div>
          </div>
        </>
      ) : null}

      {data.trackers.length > 0 ? (
        <>
          <div className="mt-8">
            <SectionHeader>Long-running work</SectionHeader>
          </div>
          <ul className="card divide-y divide-border">
            {data.trackers.map((tracker) => (
              <li key={tracker.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-fg">{tracker.title}</span>
                <div className="flex items-center gap-3">
                  {tracker.lastError ? <Pill tone="red">Sync failed</Pill> : null}
                  <span className="text-sm text-fg-muted">
                    {tracker.done ?? '—'} / {tracker.total ?? '—'}
                    {tracker.percent !== null ? ` · ${tracker.percent}%` : ' · unsynced'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <div className="mt-8">
        <SectionHeader>What changed</SectionHeader>
      </div>
      {data.recentChanges.length === 0 ? (
        <EmptyState title="No project changes in this period" />
      ) : (
        <ul className="card divide-y divide-border">
          {data.recentChanges.slice(0, 15).map((change, index) => (
            <li key={`${change.occurredAt}-${index}`} className="px-4 py-2 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-fg">{change.reason || change.action.replace('_', ' ')}</span>
                <span className="text-xs text-fg-muted">
                  {new Date(change.occurredAt).toLocaleString()} · {change.actor}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
